/**
 * A lightweight caching utility for API responses with TTL (time-to-live) support.
 * Uses localStorage as the storage backend.
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Default TTL: 24 hours in milliseconds
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

// Cache key prefix to avoid conflicts with other localStorage keys
const CACHE_PREFIX = 'cache_';

/**
 * Set a value in the cache with optional TTL
 * @param key - Cache key
 * @param data - Data to cache
 * @param ttlMs - Time to live in milliseconds (default: 24 hours)
 */
export function setCache<T>(
  key: string,
  data: T,
  ttlMs: number = DEFAULT_TTL_MS
): void {
  const cacheKey = CACHE_PREFIX + key;
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
    expiresAt: Date.now() + ttlMs,
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (error) {
    // Handle localStorage quota exceeded or other errors silently
    console.warn('Failed to set cache:', error);
  }
}

/**
 * Get a value from the cache if it exists and hasn't expired
 * @param key - Cache key
 * @returns The cached data or null if not found/expired
 */
export function getCache<T>(key: string): T | null {
  const cacheKey = CACHE_PREFIX + key;

  try {
    const stored = localStorage.getItem(cacheKey);

    if (!stored) {
      return null;
    }

    const entry: CacheEntry<T> = JSON.parse(stored);

    // Check if cache has expired
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return entry.data;
  } catch (error) {
    console.warn('Failed to get cache:', error);
    return null;
  }
}

/**
 * Check if a cache entry exists and is valid
 * @param key - Cache key
 * @returns true if cache exists and is not expired
 */
export function hasValidCache(key: string): boolean {
  return getCache(key) !== null;
}

/**
 * Remove a specific cache entry
 * @param key - Cache key
 */
export function removeCache(key: string): void {
  const cacheKey = CACHE_PREFIX + key;
  localStorage.removeItem(cacheKey);
}

/**
 * Clear all cache entries (those with the cache prefix)
 */
export function clearAllCache(): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * Cache keys for the application
 * Centralized location for all cache key constants
 */
export const CACHE_KEYS = {
  LIST_JABATAN: 'list_jabatan',
  LIST_PANGKAT: 'list_pangkat',
  LIST_AGAMA: 'list_agama',
  LIST_STATUS_KAWIN: 'list_status_kawin',
  LIST_STATUS_PEGAWAI: 'list_status_pegawai',
  LIST_PELATIHAN_RELEVAN: 'list_pelatihan_relevan',
  LIST_PROVINSI: 'list_provinsi',
} as const;

/**
 * TTL constants for different cache types
 */
export const CACHE_TTL = {
  // Master data rarely changes - cache for 24 hours
  MASTER_DATA: 24 * 60 * 60 * 1000,
  // Short-lived data - cache for 5 minutes
  SHORT: 5 * 60 * 1000,
  // Medium-lived data - cache for 1 hour
  MEDIUM: 60 * 60 * 1000,
} as const;
